import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Heart, Sparkles, Users, ChevronRight, ChevronLeft, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/context/CurrencyContext';
import axios from 'axios';
import GiftingWizard from '@/components/gifting/GiftingWizard';
import HomepagePartnerCTA from '@/components/home/HomepagePartnerCTA';
import useSEO from '@/utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  useSEO(); // Apply SEO for this page
  
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [journalPosts, setJournalPosts] = useState([]);
  const [impactStats, setImpactStats] = useState(null);
  const [showGiftingWizard, setShowGiftingWizard] = useState(false);
  const { formatPrice, currency } = useCurrency();

  useEffect(() => {
    fetchData();
  }, [currency]);

  const fetchData = async () => {
    try {
      const [featured, trending, impact, journal] = await Promise.all([
        axios.get(`${API}/products`, { params: { is_featured: true, limit: 4, currency } }),
        axios.get(`${API}/products`, { params: { is_trending: true, limit: 4, currency } }),
        axios.get(`${API}/impact/stats`),
        axios.get(`${API}/journal`, { params: { limit: 6 } })
      ]);
      setFeaturedProducts(featured.data.products || []);
      setTrendingProducts(trending.data.products || []);
      setImpactStats(impact.data);
      setJournalPosts(journal.data.posts || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src="/images/products/desert_sunset_flying.jpg" 
            alt="Chaleur - Luxury scarf flowing in desert sunset"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>
        
        <div className="relative chaleur-container pt-32 pb-24">
          <div className="max-w-2xl">
            <p className="caption text-white/90 mb-4 animate-fade-in-up tracking-[0.2em]">Embrace Giving, Embody Ownership</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light mb-6 text-balance animate-fade-in-up stagger-1 text-white hero-text-shadow">
              Where Every Thread Tells a <span className="editorial-italic">Story</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 animate-fade-in-up stagger-2">
              Handcrafted luxury scarves and wraps from India's finest artisans. 
              Each piece carries warmth, purpose, and timeless elegance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Button asChild className="bg-white text-foreground hover:bg-white/90 h-12 px-8 uppercase tracking-[0.15em] text-xs font-medium rounded-none">
                <Link to="/shop" data-testid="shop-now-btn">
                  Explore Collection <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                className="bg-transparent border border-white text-white h-12 px-8 uppercase tracking-[0.15em] text-xs font-medium hover:bg-white hover:text-foreground transition-all duration-500 rounded-none"
                onClick={() => setShowGiftingWizard(true)}
                data-testid="find-gift-btn"
              >
                <Gift className="mr-2 w-4 h-4" /> Find the Perfect Gift
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border border-white/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">
            <div>
              <p className="caption text-primary mb-4">The Chaleur Story</p>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Born from Warmth, <span className="editorial-italic">Crafted with Purpose</span>
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Chaleur embodies the essence of generosity, kindness, and selflessness. 
                We believe true ownership is not in what you possess, but in what you give to others. 
                Every scarf, wrap, and drape is a celebration of India's rich textile heritage, 
                handwoven by master artisans whose skills have been passed down through generations.
              </p>
              <Link to="/about" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors group">
                <span className="link-hover">Discover Our Story</span>
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img 
                src="/images/products/artisan_loom_cream.jpg"
                alt="Artisan weaving Chaleur scarf at traditional loom"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="section-spacing" data-testid="featured-section">
        <div className="chaleur-container">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="caption text-primary mb-4">Curated Selection</p>
              <h2 className="text-3xl md:text-4xl font-light">Featured Collection</h2>
            </div>
            <Link to="/shop" className="hidden md:flex items-center text-primary hover:text-primary/80 transition-colors group">
              <span className="link-hover">View All</span>
              <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featuredProducts.length > 0 ? featuredProducts.map((product, idx) => (
              <ProductCard key={product.id} product={product} formatPrice={formatPrice} index={idx} />
            )) : (
              // Placeholder cards
              [...Array(4)].map((_, idx) => (
                <div key={idx} className="product-card">
                  <div className="product-card-image bg-secondary animate-pulse" />
                  <div className="mt-4 space-y-2">
                    <div className="h-4 bg-secondary rounded w-3/4" />
                    <div className="h-4 bg-secondary rounded w-1/2" />
                  </div>
                </div>
              ))
            )}
          </div>

          <Link to="/shop" className="md:hidden flex items-center justify-center mt-8 text-primary hover:text-primary/80 transition-colors">
            <span>View All Products</span>
            <ChevronRight className="ml-1 w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* The Promise */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-white/60 mb-4">The Chaleur Promise</p>
            <h2 className="text-3xl md:text-4xl font-light">What Sets Us Apart</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl mb-4">Authentic Craftsmanship</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Each piece is handwoven by skilled artisans using traditional techniques 
                passed down through generations.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-xl mb-4">Meaningful Impact</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Every purchase supports artisan communities, providing sustainable 
                livelihoods and preserving traditional crafts.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
                <Gift className="w-8 h-8" />
              </div>
              <h3 className="text-xl mb-4">Gifting with Purpose</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Premium gift packaging and personal touches make every 
                Chaleur piece a memorable gift experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guided Gifting CTA */}
      <section className="section-spacing bg-accent/30">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/products/bridal_coffee.jpg"
                alt="Chaleur scarf with wedding rings and coffee - luxury gifting"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="md:pl-12">
              <p className="caption text-primary mb-4">Need Help with Gifting?</p>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Find the <span className="editorial-italic">Perfect</span> Gift
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Tell us who you're shopping for, the occasion, and your budget. 
                We'll curate personalized recommendations that carry warmth and meaning.
              </p>
              <Button 
                className="btn-chaleur-primary"
                onClick={() => setShowGiftingWizard(true)}
                data-testid="start-gifting-btn"
              >
                <Gift className="mr-2 w-4 h-4" /> Start Gifting Guide
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Artisan Video Showcase */}
      <section className="section-spacing bg-foreground text-primary-foreground overflow-hidden">
        <div className="chaleur-container">
          <div className="text-center mb-12">
            <p className="caption text-white/60 mb-4">The Art of Creation</p>
            <h2 className="text-3xl md:text-4xl font-light">Watch Our Artisans at Work</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-video rounded-lg overflow-hidden">
              <video 
                className="w-full h-full object-cover"
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/images/products/artisan_weaver_man.jpg"
              >
                <source src="/videos/artisan_craft_1.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden">
              <video 
                className="w-full h-full object-cover"
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/images/products/artisan_embroidery_woman.jpg"
              >
                <source src="/videos/artisan_craft_2.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/the-craft" className="inline-flex items-center text-white/80 hover:text-white transition-colors group">
              <span className="link-hover">Explore Our Craft</span>
              <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="section-spacing" data-testid="impact-section">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">How Your Purchase Gives Back</p>
            <h2 className="text-3xl md:text-4xl font-light">The Ripple Effect</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {impactStats?.artisans_supported || '200'}+
              </div>
              <p className="caption text-muted-foreground">Artisans Supported</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {impactStats?.families_impacted || '500'}+
              </div>
              <p className="caption text-muted-foreground">Families Impacted</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {impactStats?.sustainable_materials_kg || '5000'}kg
              </div>
              <p className="caption text-muted-foreground">Sustainable Materials</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {impactStats?.carbon_offset_tons || '50'}t
              </div>
              <p className="caption text-muted-foreground">Carbon Offset</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/impact" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors group">
              <span className="link-hover">Explore The Guild</span>
              <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Journal Carousel */}
      {journalPosts.length > 0 && (
        <section className="section-spacing bg-secondary/30" data-testid="journal-carousel-section">
          <div className="chaleur-container">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="caption text-primary mb-4">From The Journal</p>
                <h2 className="text-3xl md:text-4xl font-light">Stories of Craft & <span className="editorial-italic">Purpose</span></h2>
              </div>
              <Link to="/journal" className="hidden md:flex items-center text-primary hover:text-primary/80 transition-colors group">
                <span className="link-hover">View All Articles</span>
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <JournalCarousel posts={journalPosts} />

            <Link to="/journal" className="md:hidden flex items-center justify-center mt-8 text-primary hover:text-primary/80 transition-colors">
              <span>View All Articles</span>
              <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Trending Products */}
      {trendingProducts.length > 0 && (
        <section className="section-spacing bg-secondary/30">
          <div className="chaleur-container">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="caption text-primary mb-4">This Month's Favourites</p>
                <h2 className="text-3xl md:text-4xl font-light">Trending Now</h2>
              </div>
              <Link to="/shop?trending=true" className="hidden md:flex items-center text-primary hover:text-primary/80 transition-colors group">
                <span className="link-hover">View All</span>
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {trendingProducts.map((product, idx) => (
                <ProductCard key={product.id} product={product} formatPrice={formatPrice} index={idx} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Channel Partner Entry Point — near-bottom, before Gifting Wizard modal */}
      <HomepagePartnerCTA />

      {/* Gifting Wizard Modal */}
      <GiftingWizard open={showGiftingWizard} onOpenChange={setShowGiftingWizard} />
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, formatPrice, index }) => {
  const variant = product.variants?.[0];
  const price = variant?.price || 0;
  const comparePrice = variant?.compare_at_price;
  const imageUrl = product.images?.[0]?.startsWith('/') 
    ? product.images[0] 
    : (product.images?.[0] || '/images/products/IMG_6195.jpg');

  return (
    <Link 
      to={`/product/${product.slug}`} 
      className="product-card animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="product-card-image">
        <img 
          src={imageUrl}
          alt={product.name}
          loading="lazy"
        />
        {product.is_limited_edition && (
          <span className="absolute top-4 left-4 bg-foreground text-background caption px-3 py-1">
            Limited Edition
          </span>
        )}
      </div>
      <div className="mt-4 space-y-1">
        <h3 className="text-sm font-medium line-clamp-1">{product.name}</h3>
        <p className="text-xs text-muted-foreground">{product.craft_technique || 'Handcrafted'}</p>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{formatPrice(price)}</span>
          {comparePrice && (
            <span className="text-xs text-muted-foreground line-through">{formatPrice(comparePrice)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

// Journal Carousel Component
const JournalCarousel = ({ posts }) => {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScroll);
      return () => carousel.removeEventListener('scroll', checkScroll);
    }
  }, [posts]);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative group">
      {/* Navigation Buttons */}
      <button
        onClick={() => scroll('left')}
        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-background/90 backdrop-blur-sm border border-border flex items-center justify-center transition-all duration-300 hover:bg-background hover:border-primary ${
          canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } -translate-x-6 shadow-lg`}
        aria-label="Scroll left"
        data-testid="journal-carousel-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <button
        onClick={() => scroll('right')}
        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-background/90 backdrop-blur-sm border border-border flex items-center justify-center transition-all duration-300 hover:bg-background hover:border-primary ${
          canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } translate-x-6 shadow-lg`}
        aria-label="Scroll right"
        data-testid="journal-carousel-next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Carousel Container */}
      <div
        ref={carouselRef}
        className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {posts.map((post, idx) => (
          <Link
            key={post.id}
            to={`/journal/${post.slug}`}
            className="flex-shrink-0 w-[320px] md:w-[380px] snap-start group/card animate-fade-in-up"
            style={{ animationDelay: `${idx * 0.1}s` }}
            data-testid={`journal-card-${post.id}`}
          >
            <div className="aspect-[4/3] overflow-hidden mb-4">
              <img
                src={post.featured_image || '/images/products/santorini_collection.jpg'}
                alt={post.title}
                className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="space-y-2">
              <span className="caption text-primary">{post.category}</span>
              <h3 className="text-lg font-light line-clamp-2 group-hover/card:text-primary transition-colors">
                {post.title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
                <Calendar className="w-3 h-3" />
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default HomePage;
